import { useState, useRef, useEffect } from 'react';
import { formatRefs, displayId } from '../controlRefs';

// Multi-select van controls (normonderdelen), gegroepeerd per sectie, met zoekveld.
// props: controls [{id,control_id,section,title}] in normvolgorde, value (array ids), onChange(ids),
//        usedElsewhere Map(id → ['S1', …]) — lichtgrijs getoond (al gekozen in een andere sessie),
//        disabledIds Set — niet aanvinkbaar (bv. al hoofdsessie in deze sessie), placeholder, idPrefix
export default function ControlMultiSelect({
  controls = [], value = [], onChange, usedElsewhere = new Map(), disabledIds = new Set(),
  placeholder, searchPlaceholder, idPrefix = 'ctrl',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const sel = new Set(value.map(Number));
  const toggle = (id) => {
    const s = new Set(sel);
    s.has(id) ? s.delete(id) : s.add(id);
    onChange(controls.filter(c => s.has(c.id)).map(c => c.id));
  };
  const toggleSection = (ids) => {
    const s = new Set(sel);
    const all = ids.every(id => s.has(id));
    ids.forEach(id => (all ? s.delete(id) : s.add(id)));
    onChange(controls.filter(c => s.has(c.id)).map(c => c.id));
  };

  const needle = q.trim().toLowerCase();
  const shown = controls.filter(c => !disabledIds.has(c.id) || sel.has(c.id)).filter(c => !needle
    || displayId(c.control_id).toLowerCase().includes(needle.replace(/^a\./, 'a'))
    || c.control_id.toLowerCase().includes(needle)
    || (c.title || '').toLowerCase().includes(needle));
  const sections = [...new Set(shown.map(c => c.section))];
  const summary = formatRefs(value, controls);

  return (
    <div className="position-relative" ref={ref}>
      <button type="button" className="form-select form-select-sm text-start"
        onClick={() => setOpen(o => !o)} style={{ minHeight: 31 }}>
        <span className={summary ? '' : 'text-muted'}>{summary || placeholder}</span>
      </button>
      {open && (
        <div className="card shadow position-absolute mt-1 w-100" style={{ zIndex: 1060 }}>
          <div className="p-2 border-bottom">
            <input className="form-control form-control-sm" autoFocus placeholder={searchPlaceholder}
              value={q} onChange={e => setQ(e.target.value)} />
          </div>
          <div className="p-2" style={{ maxHeight: 320, overflowY: 'auto' }}>
            {sections.length === 0 && <div className="text-muted small px-1">—</div>}
            {sections.map(sec => {
              const list = shown.filter(c => c.section === sec);
              const ids = list.filter(c => !disabledIds.has(c.id)).map(c => c.id);
              const allOn = ids.length > 0 && ids.every(id => sel.has(id));
              return (
                <div key={sec} className="mb-2">
                  <div className="d-flex justify-content-between align-items-center px-1 mb-1"
                    style={{ position: 'sticky', top: -8, background: '#fff', zIndex: 1 }}>
                    <span className="fw-semibold small">{sec}</span>
                    {ids.length > 1 && (
                      <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 11 }}
                        onClick={() => toggleSection(ids)}>
                        {allOn ? '− ' : '+ '}{sec}
                      </button>
                    )}
                  </div>
                  {list.map(c => {
                    const used = usedElsewhere.get(c.id);
                    const grey = used && !sel.has(c.id);
                    return (
                      <div className="form-check d-flex align-items-start gap-1" key={c.id}
                        style={{ opacity: grey ? 0.45 : 1 }}>
                        <input className="form-check-input mt-1" type="checkbox" id={`${idPrefix}-${c.id}`}
                          checked={sel.has(c.id)} disabled={disabledIds.has(c.id)} onChange={() => toggle(c.id)} />
                        <label className="form-check-label small flex-grow-1" htmlFor={`${idPrefix}-${c.id}`}>
                          <span className="font-mono me-1">{displayId(c.control_id)}</span>
                          <span className="text-muted">{c.title}</span>
                        </label>
                        {used && <span className="badge bg-light text-muted border ms-1" style={{ fontSize: 10 }}>{used.join(', ')}</span>}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
